import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import { config } from '@/services/config';
import { prepareHeaders, buildQueryString } from '@/state/slices/index';
import { StudiesAPI } from '@/state/slices/studies';
import type {
  CreatePatientPayload,
  MergePatientsPayload,
  Patient,
  PatientListItem,
  PatientListParams,
} from '@/models/patient';
import type { Pagination } from '@/models';

export const PatientAPI = createApi({
  reducerPath: 'PatientAPI',
  baseQuery: fetchBaseQuery({
    baseUrl: config.BASE_URL,
    prepareHeaders,
  }),
  tagTypes: ['Patients'],
  endpoints: (builder) => ({
    getPatient: builder.query<Patient, string>({
      query: (uuid) => ({
        url: `/patients/${uuid}/`,
        method: 'GET',
      }),
      providesTags: (_result, _error, uuid) => [{ type: 'Patients', id: uuid }],
    }),
    listPatients: builder.query<Pagination<PatientListItem>, PatientListParams>(
      {
        query: (params) => {
          const queryString = buildQueryString(params);
          return { url: `/patients/${queryString}`, method: 'GET' };
        },
        providesTags: (result) =>
          result
            ? [
                ...result.results.map((p) => ({
                  type: 'Patients' as const,
                  id: p.uuid,
                })),
                { type: 'Patients' as const, id: 'LIST' },
              ]
            : [{ type: 'Patients' as const, id: 'LIST' }],
      }
    ),
    createPatient: builder.mutation<Patient, CreatePatientPayload>({
      query: (body) => ({
        url: '/patients/',
        method: 'POST',
        body,
      }),
      invalidatesTags: [{ type: 'Patients', id: 'LIST' }],
    }),
    mergePatients: builder.mutation<Patient, MergePatientsPayload>({
      query: (body) => ({
        url: '/patients/merge/',
        method: 'POST',
        body,
      }),
      invalidatesTags: (_result, _error, arg) => [
        { type: 'Patients', id: 'LIST' },
        { type: 'Patients', id: arg.primaryUuid },
        { type: 'Patients', id: arg.secondaryUuid },
      ],
      // Studies are re-parented to the primary by the BE. They live in a
      // separate RTK Query slice, so cross-invalidate to refetch the
      // primary's study list (count, timeline, etc.) post-merge.
      async onQueryStarted(_arg, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
          dispatch(
            StudiesAPI.util.invalidateTags([
              { type: 'Studies', id: 'PATIENT_LIST' },
            ])
          );
        } catch {
          /* merge failed — nothing to invalidate */
        }
      },
    }),
  }),
});

export const {
  useGetPatientQuery,
  useListPatientsQuery,
  useCreatePatientMutation,
  useMergePatientsMutation,
} = PatientAPI;
